"use client";

// Subscription checkout panel — rendered instead of the one-time form when the
// cart's product is a recurring plan (type=subscription). The card is collected
// INLINE via Stripe Elements (PaymentElement) + a trial SetupIntent: $0 is
// charged today, the card is saved, and Stripe auto-charges after the free
// trial. The subscription is created server-side only on submit (deferred), so
// abandoned checkouts don't spawn ghost subscriptions. Webhooks
// (customer.subscription.* / invoice.paid) are provider truth for status.
//
// Multi-currency: amount/trial/PayPal-plan come PER CURRENCY from
// /checkout/subscription-plan (product_subscription_prices catalog).
//
// Must be rendered inside <Elements options={{ mode: 'setup', currency }}>.

import { useCallback, useEffect, useRef, useState } from "react";
import { loadPayPalSdk } from "@/lib/paypal-sdk-loader";
import { ChevronDown } from "lucide-react";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { friendlyPaymentError } from "@/lib/payment-error";

const GO_API =
  process.env.NEXT_PUBLIC_API_URL || "https://crm-app.makedesignerbags.com";

// Where a buyer who already has a membership is sent. Hardcoded on purpose:
// there is no NEXT_PUBLIC_MEMBER_APP_BASE in any deploy artefact, and a config
// read with no writer is a promise the deployment does not keep. The member
// area lives on members.makerspark.io (owner decision 2026-09-21, the same host
// the cancellation mails use).
const MEMBER_ACCOUNT_URL = "https://members.makerspark.io";

interface SubscriptionPlan {
  available: boolean;
  reason?: string;
  currency: string;
  amount_cents: number;
  original_amount_cents?: number;
  interval?: string;
  trial_days: number;
  // Present (true) when trial_days is 0 BECAUSE this buyer already used the
  // free trial — the panel then explains why instead of silently flipping.
  trial_gated?: boolean;
  paypal_plan_id?: string;
  stripe_ready: boolean;
  paypal_ready: boolean;
}

interface Props {
  productId: string;
  currency: string; // active checkout currency (e.g. "eur"); selects the plan
  slug: string;
  lpId: string;
  visitorId: string;
  email: string;
  setEmail: (v: string) => void;
  firstName: string;
  setFirstName: (v: string) => void;
  lastName: string;
  setLastName: (v: string) => void;
  billingCountry: string;
  setBillingCountry: (v: string) => void;
  countries: { code: string; name: string }[];
  clientId: string;
  paypalEnv: "live" | "sandbox";
  // AddPaymentInfo hook (Shopify parity): called at the real pay attempt
  // (trial start / subscribe click) so the Meta/Google/TikTok AddPaymentInfo
  // pixels fire for subscriptions too — the one-time form gets this via its
  // `track` prop, but this panel posts payment_initiated on its own pipe.
  onPaymentInitiated?: () => void;
  // Native inline checkout: it collects contact in its own step, so suppress
  // this panel's contact inputs and route email-required to the island's field.
  hideContact?: boolean;
  /** Surface label on the funnel events — "island" when rendered inside the
   *  inline checkout, so reporting can tell the two surfaces apart. */
  funnelMode?: "page" | "island";
  onEmailRequired?: () => void;
  // South-Africa PPP display (trusted-ZA only): saZarCents = owner's curated ZAR
  // for this plan (e.g. R149) shown instead of the USD; saBilledUsdCents = the
  // real USD charge shown in the "billed in USD" note. The CHARGE stays `currency`
  // (USD). Both undefined → no SA (normal per-currency display).
  saZarCents?: number;
  saBilledUsdCents?: number;
}

const PRICE_LOCALES: Record<string, string> = {
  USD: "en-US", EUR: "de-DE", GBP: "en-GB", CHF: "de-CH", CAD: "en-CA", AUD: "en-AU",
};
function formatPrice(cents: number, currency: string): string {
  const cur = currency.toUpperCase();
  try {
    return new Intl.NumberFormat(PRICE_LOCALES[cur] || "en-US", { style: "currency", currency: cur }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${cur}`;
  }
}

export function SubscriptionCheckout({
  productId,
  currency,
  slug,
  lpId,
  visitorId,
  email,
  setEmail,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  billingCountry,
  setBillingCountry,
  countries,
  clientId,
  paypalEnv,
  onPaymentInitiated,
  saZarCents,
  saBilledUsdCents,
  hideContact = false,
  funnelMode = "page",
  onEmailRequired,
}: Props) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [planLoading, setPlanLoading] = useState(true);
  const ppRef = useRef<HTMLDivElement>(null);

  const emailRef = useRef(email);
  // PayPal requires a typed email (contact identity + abandoned-cart). The
  // flag renders the red border + hint; the input ref lets the PayPal click
  // scroll/focus the field. Cleared automatically once the email is valid.
  const [emailError, setEmailError] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const ppEmailAbortRef = useRef(false);
  const onEmailRequiredRef = useRef(onEmailRequired);
  onEmailRequiredRef.current = onEmailRequired;
  const firstRef = useRef(firstName);
  const lastRef = useRef(lastName);
  const countryRef = useRef(billingCountry);
  useEffect(() => { emailRef.current = email; }, [email]);
  useEffect(() => {
    if (emailError && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) setEmailError(false);
  }, [email, emailError]);
  useEffect(() => { firstRef.current = firstName; }, [firstName]);
  useEffect(() => { lastRef.current = lastName; }, [lastName]);
  useEffect(() => { countryRef.current = billingCountry; }, [billingCountry]);

  // Trial gate: once a valid email is typed (debounced), the plan is
  // re-resolved WITH it — a prior monthly subscriber gets trial_days=0 and the
  // trial-less PayPal plan (or no PayPal button until that plan is seeded).
  const [eligEmail, setEligEmail] = useState("");
  useEffect(() => {
    const v = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { setEligEmail(""); return; }
    const t = window.setTimeout(() => setEligEmail(v.toLowerCase()), 600);
    return () => window.clearTimeout(t);
  }, [email]);
  // A trial_not_available rejection from the PayPal record backstop (payer
  // email known-used while the typed email was fresh): re-offer on the
  // trial-less plan ("" hides the button until the owner seeds it).
  const [gatePlanOverride, setGatePlanOverride] = useState<string | null>(null);

  // Duplicate gate (2026-09-24): the server refuses a second membership for an
  // address that already has a live one. This is a dead end for the checkout,
  // not a retryable error, so the pay buttons go away and the buyer is pointed
  // at their account. Measured cause: both providers minted a subscription per
  // press of the pay button, and 16 buyers ended up paying twice.
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);

  // Resolve the per-currency plan whenever product/currency/email changes.
  // With an email the request is a POST (body) so the address never lands in
  // URL/access logs; without one it stays the plain GET. Email-triggered
  // re-fetches keep the already-rendered plan on screen (no skeleton/disabled
  // flicker while the buyer types) — hasPlanRef tracks that.
  const hasPlanRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    if (!hasPlanRef.current) setPlanLoading(true);
    const url = `${GO_API}/api/v1/checkout/subscription-plan`;
    const req = eligEmail
      ? fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ product_id: productId, currency: currency.toUpperCase(), email: eligEmail }),
        })
      : fetch(`${url}?product_id=${encodeURIComponent(productId)}&currency=${encodeURIComponent(currency.toUpperCase())}`);
    req
      .then((r) => r.json())
      .then((j) => {
        if (!cancelled) {
          setPlan((j.data as SubscriptionPlan) || null);
          hasPlanRef.current = !!j.data;
          setGatePlanOverride(null);
        }
      })
      .catch(() => {
        // Keep the last good plan on a transient re-fetch failure; only a
        // first load with no plan collapses to the unavailable branch.
        if (!cancelled && !hasPlanRef.current) setPlan(null);
      })
      .finally(() => { if (!cancelled) setPlanLoading(false); });
    return () => { cancelled = true; };
  }, [productId, currency, eligEmail]);

  const validateEmail = useCallback((): boolean => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailRef.current.trim())) {
      if (onEmailRequiredRef.current) onEmailRequiredRef.current();
      else setErrorMsg("Please enter your email.");
      return false;
    }
    return true;
  }, []);

  const planCur = (plan?.currency || currency).toUpperCase();
  const available = !!plan?.available;
  const trialDays = gatePlanOverride !== null ? 0 : (plan?.trial_days ?? 0);
  // South-Africa PPP: show the owner's curated ZAR (saZarCents) while the CHARGE
  // stays planCur (USD). No FX rate here, so the compare-at strike is dropped in
  // ZA mode (the reduced price IS the deal); the "billed in USD" note shows the
  // real charge.
  const za = (saZarCents || 0) > 0;
  const dCur = za ? "ZAR" : planCur;
  const amtCents = za ? (saZarCents as number) : (plan?.amount_cents ?? 0);
  const amountLabel = plan ? `${formatPrice(amtCents, dCur)}/mo` : "";
  const hasOriginal = !za && !!(plan?.original_amount_cents && plan.original_amount_cents > plan.amount_cents);
  const originalLabel = hasOriginal ? `${formatPrice(plan!.original_amount_cents!, planCur)}/mo` : "";
  const zeroLabel = formatPrice(0, dCur);
  const saBilledLabel = za && saBilledUsdCents ? formatPrice(saBilledUsdCents, "USD") : "";
  const stripeReady = !!plan?.stripe_ready;
  const paypalPlanId = gatePlanOverride !== null
    ? gatePlanOverride
    : plan?.paypal_ready ? plan?.paypal_plan_id || "" : "";

  // Trial-ineligible buyers pay the plan price NOW: the deferred Elements
  // group must be in mode=payment (amount+currency) so confirmPayment works;
  // eligible buyers keep the parent's mode=setup ($0 SetupIntent). update()
  // reconfigures the mounted group in place. elementsModeRef tracks the
  // CURRENT group mode so startStripeTrial can re-align with the server's
  // authoritative intent_type before confirming (the client-side gate is a
  // debounced display heuristic and can lag the server's decision).
  const elementsModeRef = useRef<"setup" | "payment">("setup");
  const applyElementsMode = useCallback(
    (mode: "setup" | "payment", amountCents: number, cur: string) => {
      if (!elements || elementsModeRef.current === mode) return;
      try {
        if (mode === "payment" && amountCents > 0) {
          elements.update({ mode: "payment", amount: amountCents, currency: cur.toLowerCase() });
          elementsModeRef.current = "payment";
        } else if (mode === "setup") {
          elements.update({ mode: "setup" });
          elementsModeRef.current = "setup";
        }
      } catch {
        // Never let an Elements reconfigure error break the panel render;
        // startStripeTrial re-aligns against the server response anyway.
      }
    },
    [elements],
  );
  useEffect(() => {
    if (!plan?.available) return;
    applyElementsMode(trialDays === 0 ? "payment" : "setup", plan.amount_cents ?? 0, planCur);
  }, [plan, trialDays, planCur, applyElementsMode]);

  // Stripe: collect the card inline, then create the trial subscription +
  // confirm the SetupIntent ($0 today). Deferred — the subscription is only
  // created on submit.
  // payment_initiated — best-effort funnel beacon on the LP pipe, fired when
  // the buyer actually starts the trial/subscription payment. visitorId is
  // read through a ref: the PayPal Buttons closure registers ONCE and would
  // otherwise capture an empty initial visitorId forever.
  const piSentRef = useRef(false);
  const visitorIdRef2 = useRef(visitorId);
  visitorIdRef2.current = visitorId;
  // One-shot latch for the PayPal button: see createSubscription below.
  const ppInFlightRef = useRef(false);
  // Ref so the once-registered PayPal createSubscription closure always reads
  // the latest parent callback (mirrors visitorIdRef2/emailRef).
  const onPaymentInitiatedRef = useRef(onPaymentInitiated);
  onPaymentInitiatedRef.current = onPaymentInitiated;
  function trackPaymentInitiated(method: string) {
    // AddPaymentInfo pixel parity with the one-time form: fire the parent's
    // payment-info hook on every real pay attempt (Meta/Google/TikTok),
    // independent of the first-party beacon guards below. The parent's
    // own once-refs dedupe repeated clicks.
    onPaymentInitiatedRef.current?.();
    if (piSentRef.current || !visitorIdRef2.current || !slug) return;
    piSentRef.current = true;
    fetch(`${GO_API}/api/v1/lp/${encodeURIComponent(slug)}/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        visitor_id: visitorIdRef2.current,
        // Both steps in ONE batch: the subscription panel has no separate
        // method picker, so the pay click IS the selection moment. Without the
        // selection event a subscription buyer never reaches the
        // payment_method_selected step and the session's payment_method stays
        // empty — the same blind spot the one-time paths had.
        events: [
          { type: "payment_method_selected", data: { method, mode: funnelMode }, ts: Date.now() },
          { type: "payment_initiated", data: { method, mode: funnelMode }, ts: Date.now() },
        ],
      }),
    }).catch(() => {});
  }

  async function startStripeTrial() {
    if (!stripe || !elements) return;
    if (!validateEmail()) return;
    setProcessing(true);
    setErrorMsg(null);
    trackPaymentInitiated("card_subscription");

    const { error: submitErr } = await elements.submit();
    if (submitErr) {
      setErrorMsg(submitErr.message || "Please check your card details.");
      setProcessing(false);
      return;
    }

    let clientSecret = "";
    let orderId = "";
    let intentType = "setup";
    let serverAmountCents = 0;
    let serverCurrency = planCur;
    try {
      const res = await fetch(`${GO_API}/api/v1/checkout/subscription-setup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: productId,
          currency: planCur,
          customer_email: emailRef.current.trim(),
          customer_first_name: firstRef.current,
          customer_last_name: lastRef.current,
          customer_country: countryRef.current,
          landing_page_id: lpId,
          slug,
          visitor_id: visitorId,
        }),
      });
      const json = await res.json();
      if (res.status === 409 && json?.error?.code === "already_subscribed") {
        setAlreadySubscribed(true);
        setErrorMsg(json.error.message as string);
        setProcessing(false);
        return;
      }
      if (!res.ok || !json.data?.client_secret) {
        throw new Error(json.error?.message || "Could not start the subscription");
      }
      clientSecret = json.data.client_secret;
      orderId = json.data.order_id || "";
      intentType = json.data.intent_type || "setup";
      serverAmountCents = json.data.amount_cents || 0;
      serverCurrency = json.data.currency || planCur;
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Could not start the subscription");
      setProcessing(false);
      return;
    }

    const successUrl = `/checkout/success?subscription=1${orderId ? `&order_id=${encodeURIComponent(orderId)}` : ""}`;

    // The SERVER's intent_type is authoritative: the client-side trial gate is
    // a debounced display heuristic and can lag (email pasted + instant click).
    // Re-align the Elements group before confirming — a mode change requires a
    // fresh elements.submit() — so confirmSetup/confirmPayment never runs
    // against the wrong mode.
    const wantMode: "setup" | "payment" = intentType === "payment" ? "payment" : "setup";
    if (elementsModeRef.current !== wantMode) {
      applyElementsMode(wantMode, serverAmountCents, serverCurrency);
      const { error: resubmitErr } = await elements.submit();
      if (resubmitErr) {
        setErrorMsg(resubmitErr.message || "Please check your card details.");
        setProcessing(false);
        return;
      }
    }

    // Trial → $0 SetupIntent (confirmSetup). Trial-ineligible → the first
    // invoice's PaymentIntent (confirmPayment). Same args, same terminal
    // handling; only the SDK method and the fallback copy differ.
    const confirmArgs = {
      elements,
      clientSecret,
      redirect: "if_required" as const,
      confirmParams: {
        // Only used if a redirect is required (e.g. 3DS). The PaymentElement
        // owns billing details; the customer already carries the typed email.
        return_url: `${window.location.origin}${successUrl}`,
      },
    };
    const result =
      wantMode === "payment"
        ? await stripe.confirmPayment(confirmArgs)
        : await stripe.confirmSetup(confirmArgs);
    const { error } = result;
    if (error) {
      // Bank declines get the actionable copy; validation errors keep the
      // Stripe message via the helper's fallback.
      setErrorMsg(
        error.code === "card_declined" || error.decline_code
          ? friendlyPaymentError(error)
          : error.message ||
              (wantMode === "payment"
                ? "Your payment could not be completed. Please try again."
                : "Your card could not be saved. Please try again."),
      );
      setProcessing(false);
      return;
    }
    const intent = "paymentIntent" in result ? result.paymentIntent : result.setupIntent;
    if (intent && (intent.status === "succeeded" || intent.status === "processing")) {
      window.location.href = successUrl;
      return;
    }
    setProcessing(false);
  }

  // PayPal subscription button on the per-currency plan id.
  useEffect(() => {
    if (!paypalPlanId || !clientId) {
      // The plan id can go away AFTER buttons were rendered (trial gate hid
      // PayPal for this buyer). Leaving the old buttons mounted would keep a
      // clickable closure bound to the TRIAL plan — clear the container.
      if (ppRef.current) ppRef.current.innerHTML = "";
      return;
    }
    void paypalEnv;
    const renderButtons = () => {
      const pp = (window as unknown as Record<string, unknown>).paypal as Record<string, unknown> | undefined;
      if (!pp || !ppRef.current) return;
      ppRef.current.innerHTML = "";
      const Buttons = pp.Buttons as (opts: Record<string, unknown>) => { render: (el: HTMLElement) => void };
      Buttons({
        style: { layout: "horizontal", color: "gold", shape: "rect", label: "subscribe", height: 48 },
        createSubscription: (_data: unknown, actions: { subscription: { create: (o: Record<string, unknown>) => Promise<string> } }) => {
          // Email is REQUIRED for PayPal: the typed email is the contact
          // identity and feeds the abandoned-cart sequence. Without it,
          // route the buyer to the email field instead of opening PayPal.
          // (emailRef/setEmailError/emailInputRef are stable, so this
          // once-registered closure stays correct.)
          if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailRef.current.trim())) {
            ppEmailAbortRef.current = true;
            if (onEmailRequiredRef.current) {
              onEmailRequiredRef.current();
            } else {
              setEmailError(true);
              emailInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
              window.setTimeout(() => emailInputRef.current?.focus({ preventScroll: true }), 350);
            }
            return Promise.reject(new Error("email_required"));
          }
          // One press, one PayPal agreement. PayPal mints a real subscription
          // on every create call, before approval, so an impatient second
          // press used to produce a second membership (five in three minutes
          // in the worst Live case). The latch is never cleared on success:
          // the page redirects, and a buyer who returns starts a fresh page.
          if (ppInFlightRef.current) {
            return Promise.reject(new Error("subscription_in_flight"));
          }
          ppInFlightRef.current = true;
          trackPaymentInitiated("paypal_subscription");
          return actions.subscription.create({ plan_id: paypalPlanId });
        },
        onApprove: async (data: { subscriptionID?: string }) => {
          const subID = data.subscriptionID || "";
          // Recording can take seconds (the server verifies with PayPal). The
          // card button must not be pressable in that window, or a buyer can
          // race the gate and end up with one of each.
          setProcessing(true);
          try {
            const res = await fetch(`${GO_API}/api/v1/paypal/record-subscription`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                paypal_subscription_id: subID,
                product_id: productId,
                landing_page_id: lpId,
                customer_email: emailRef.current,
                customer_first_name: firstRef.current,
                customer_last_name: lastRef.current,
                customer_country: countryRef.current,
              }),
            });
            // Trial-gate backstop: the PayPal payer email already used the free
            // trial — the server voided the subscription. Re-offer without the
            // trial (empty plan id hides PayPal until the trial-less plan exists).
            if (res.status === 409) {
              const j = await res.json().catch(() => null);
              if (j?.error?.code === "already_subscribed") {
                // The server voided the PayPal agreement it just refused, so
                // nothing recurring survives this branch.
                setAlreadySubscribed(true);
                setErrorMsg(j.error.message as string);
                setProcessing(false);
                return;
              }
              if (j?.error?.code === "trial_not_available") {
                ppInFlightRef.current = false; // a re-offer must be payable
                setGatePlanOverride(j?.paypal_plan_id_no_trial || "");
                setErrorMsg(
                  "This PayPal account already used the free trial, so the membership starts at the regular monthly price. Please complete your subscription again below — nothing was charged.",
                );
                setProcessing(false);
                return;
              }
            }
          } catch {
            // Non-fatal: the BILLING.SUBSCRIPTION.* webhook reconciles regardless.
          }
          setProcessing(false);
          window.location.href = `/checkout/success?sub=${encodeURIComponent(subID)}`;
        },
        onCancel: () => {
          // The buyer closed the PayPal window: the agreement stays
          // APPROVAL_PENDING and never bills, so the next press must work.
          ppInFlightRef.current = false;
        },
        onError: (err?: { message?: string }) => {
          // Our own "one press" rejection lands here too. Re-arming on it
          // would turn the latch into a speed bump: press, error, press again,
          // second agreement. Keep it latched and say nothing.
          if (err?.message === "subscription_in_flight") return;
          ppInFlightRef.current = false;
          // The email-required abort surfaces here too — the red email hint
          // is already showing, don't overwrite it with the generic message.
          if (ppEmailAbortRef.current) {
            ppEmailAbortRef.current = false;
            return;
          }
          setErrorMsg("PayPal could not start the subscription. Please try again.");
        },
      }).render(ppRef.current);
    };
    // Shared loader: reuses an already-loaded subscription SDK on remounts
    // and cleans the one-time SDK's zoid globals when switching intents
    // (one-time plan -> subscription plan) — see paypal-sdk-loader.ts.
    let cancelled = false;
    const tryRender = (attempt: number) => {
      if (cancelled) return;
      const pp = (window as unknown as Record<string, unknown>).paypal;
      if ((!pp || !ppRef.current) && attempt < 40) {
        // Container not committed yet (plan-switch race / loading skeleton)
        // — retry briefly instead of bailing forever.
        window.setTimeout(() => tryRender(attempt + 1), 150);
        return;
      }
      renderButtons();
    };
    loadPayPalSdk(
      "paypal-sdk-sub",
      `https://www.paypal.com/sdk/js?client-id=${clientId}&vault=true&intent=subscription`,
      () => tryRender(0)
    );
    return () => {
      cancelled = true;
    };
  }, [clientId, paypalPlanId, productId, lpId, paypalEnv]);

  const inputCls =
    "w-full px-3 py-3 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none";

  if (!planLoading && !available) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-base font-semibold text-gray-900">Start your membership</h3>
        <div className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
          This plan isn&apos;t available in {planCur} yet. Please switch the currency (top of the page) to continue.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900">
          {trialDays > 0 ? "Start your free trial" : "Start your membership"}
        </h3>

        {planLoading ? (
          <div className="mt-3 h-24 rounded-xl bg-gray-100 animate-pulse" />
        ) : trialDays > 0 ? (
          <div className="mt-3 rounded-xl border border-teal-100 bg-teal-50/60 p-4">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium text-gray-700">Due today</span>
              <span className="text-2xl font-extrabold text-teal-700">{zeroLabel}</span>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              {trialDays}-day free trial — full access, nothing charged today.
            </p>
            {za && (
              <p className="mt-1 text-xs text-gray-500">
                Prices shown in ZAR · billed in USD ({saBilledLabel}/mo)
              </p>
            )}
            <div className="mt-3 flex items-baseline justify-between border-t border-teal-100 pt-3">
              <span className="text-sm text-gray-600">Then</span>
              <span className="text-sm font-semibold text-gray-900">
                {hasOriginal && (
                  <span className="mr-1.5 font-normal text-gray-400 line-through">{originalLabel}</span>
                )}
                {amountLabel}
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Cancel anytime before the trial ends and you won&apos;t be charged.
            </p>
          </div>
        ) : (
          <>
            {(plan?.trial_gated || gatePlanOverride !== null) && (
              <div className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                The free trial was already used on this account, so it can&apos;t be claimed
                again — your membership starts right away at {amountLabel}.
              </div>
            )}
            <p className="text-sm text-gray-500 mt-1">
              {hasOriginal && <span className="mr-1.5 text-gray-400 line-through">{originalLabel}</span>}
              {amountLabel}. Cancel anytime.
            </p>
            {/* SA disclosure must survive the no-trial arm too: a trial-gated
                ZA buyer is charged USD immediately while seeing ZAR. */}
            {za && (
              <p className="mt-1 text-xs text-gray-500">
                Prices shown in ZAR · billed in USD ({saBilledLabel}/mo)
              </p>
            )}
          </>
        )}
      </div>

      {!hideContact && (
      <>
      <div>
        <input
          ref={emailInputRef}
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={(e) => {
            // No native form submit ever happens here, so type="email" alone
            // shows no browser bubble: flag invalid non-empty input on blur.
            const v = e.target.value.trim();
            if (v && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) setEmailError(true);
          }}
          className={
            emailError
              ? "w-full px-3 py-3 border-2 border-red-500 rounded-lg text-sm bg-white focus:ring-2 focus:ring-red-400 focus:border-red-500 outline-none"
              : inputCls
          }
        />
        {emailError && (
          <p className="mt-1.5 text-xs font-medium text-red-600">Enter an email</p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <input type="text" name="given-name" autoComplete="given-name" placeholder="First name (optional)" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputCls} />
        <input type="text" name="family-name" autoComplete="family-name" placeholder="Last name (optional)" value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputCls} />
      </div>
      <div className="relative">
        <select value={billingCountry} onChange={(e) => setBillingCountry(e.target.value)} name="country" autoComplete="country" className={`${inputCls} appearance-none pr-10`}>
          <option value="">Select country / region…</option>
          {countries.map((c) => (
            <option key={c.code} value={c.code}>{c.name}</option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      </div>
      </>
      )}

      {/* Inline card fields (Stripe PaymentElement) — $0 today, saved for after the trial.
          terms:'never' hides Stripe's auto "…will charge your card for future payments…"
          mandate text; our own terms ("Cancel anytime" + Terms link) cover it. */}
      {stripeReady && (
        <div className="pt-1">
          <PaymentElement options={{ layout: "tabs", terms: { card: "never", sepaDebit: "never" } }} />
        </div>
      )}

      {errorMsg && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{errorMsg}</div>
      )}

      {/* Duplicate gate: the address already has a live membership. The pay
          buttons are gone (not merely disabled) so a second one cannot be
          started from this page, and the buyer gets the one link that helps. */}
      {alreadySubscribed && (
        <a
          href={MEMBER_ACCOUNT_URL}
          className="w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold py-4 px-6 rounded-xl text-base transition-colors"
        >
          Go to my account
        </a>
      )}

      {stripeReady && !alreadySubscribed && (
        <button
          type="button"
          onClick={startStripeTrial}
          disabled={processing || planLoading || !stripe}
          className="w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-semibold py-4 px-6 rounded-xl text-base transition-colors"
        >
          {processing ? "Processing…" : trialDays > 0 ? `Start ${trialDays}-day free trial` : "Subscribe"}
        </button>
      )}

      {stripeReady && paypalPlanId && !alreadySubscribed && (
        <div className="text-center text-xs uppercase tracking-wider text-gray-400">or</div>
      )}
      <div ref={ppRef} className={alreadySubscribed ? "hidden" : undefined} />
      {/* Recurring-charge disclosure — one muted line under the pay button(s). */}
      {available && plan && !alreadySubscribed && (
        <p className="text-xs text-gray-500 text-center">
          {trialDays > 0
            ? `Free for ${trialDays} days, then ${formatPrice(amtCents, dCur)}/month. Cancel anytime.`
            : `${formatPrice(amtCents, dCur)}/month. Cancel anytime.`}
        </p>
      )}
      <p className="text-xs text-gray-400 text-center">
        Secure checkout · {planCur} · powered by Stripe &amp; PayPal
      </p>
    </div>
  );
}
